
import React, { useEffect, useState } from 'react'
import { Card, List, Button, Input, Space, Modal, message, Empty, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { GeneralService } from '../../Services/GeneralService'
import { notify } from '../Customs/NotifyProvider'
import { formatDate } from '../Customs/dateFormatter'

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../utils/permissionUtils'

const MODULE_KEY = 'amenities'

const ListAllAm = () => {
    const [amenities, setAmenities] = useState([])
    const [newAmenity, setNewAmenity] = useState('')
    const [search, setSearch] = useState('')
    const [editing, setEditing] = useState(null)
    const [editingValue, setEditingValue] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)

    const loadAmenities = async () => {
        try {
            const response = await GeneralService.getAllAmenities()
            if (response.status) {
                setAmenities(response.data)
            } else {
                console.error('Failed to load amenities: ' + response.message)
            }
        } catch (error) {
            console.error('Error loading amenities: ' + error.message)
        }
    }

    useEffect(() => {
        loadAmenities()
    }, [])

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />
    }

    const persist = (next) => {
        setAmenities(next)
    }

    const handleAdd = () => {
        const value = newAmenity.trim()
        if (!value) {
            notify.warning('Enter an amenity name to add')
            return
        }

        const payload = {
            name: value,
            added_on: formatDate(),
            updated_on: formatDate(),
        }

        console.log(payload);


        if (amenities.some(a => a.name === value)) {
            notify.warning('Amenity already added')
            return
        }

        const request = GeneralService.createAmenity(payload)
        request.then((response) => {
            if (response.status) {
                persist([...amenities, ...response.data])
                setNewAmenity('')
                notify.success('Amenity added')
            } else {
                notify.error('Failed to add amenity: ', response.message)
            }
        }).catch((error) => {
            console.error('Error adding amenity: ' + error.message)
            notify.error('Something went wrong while adding amenity', error.message);
        })


    }

    const openEdit = (item) => {
        setEditing(item.id)
        setEditingValue(item.name)
        setIsModalOpen(true)
    }

    const handleEditSave = async () => {
        const value = editingValue.trim()
        if (!value) {
            notify.warning('Amenity cannot be empty')
            return
        }

        const payload = {
            name: value,
            updated_on: formatDate(),
        }

        try {
            const response = await GeneralService.editAmenity(editing, payload)
            if (response.status) {
                const updated = amenities.map((a) => (a.id === editing ? { ...a, name: value, updated_on: formatDate() } : a))
                persist(updated)
                setIsModalOpen(false)
                setEditing(null)
                setEditingValue('')
                notify.success('Amenity updated')
            } else {
                notify.error('Failed to update amenity: ' + response.message)
            }
        } catch (error) {
            console.error('Error updating amenity: ' + error.message)
            notify.error('Something went wrong while updating amenity')
        }
    }

    const handleDelete = async (item) => {
        try {
            const response = await GeneralService.delete('amenities', item.id)
            if (response.status) {
                const next = amenities.filter((a) => a.id !== item.id)
                persist(next)
                notify.success('Amenity deleted')
            } else {
                notify.error('Failed to delete amenity: ' + response.message)
            }
        } catch (error) {
            console.error('Error deleting amenity: ' + error.message)
            notify.error('Something went wrong while deleting amenity')
        }
    }

    return (
        <Card title="Amenities" style={{ maxWidth: 800, margin: '0 auto' }}
            extra={
                <Input
                    size='large'
                    placeholder="Search amenities"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
            }
        >


            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Input
                    size='large'
                    allowClear
                    placeholder="Add amenity (e.g., Private bathroom)"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onPressEnter={handleAdd}
                    style={{ width: 420 }}
                />
                {canCreateModule(MODULE_KEY) && (
                    <Button size='large' type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add
                    </Button>
                )}
            </Space>

            <List
                bordered
                dataSource={amenities.filter((item) =>
                    item.name.toLowerCase().includes(search.trim().toLowerCase())
                )}
                locale={{ emptyText: 'No amenities found!' }}
                renderItem={(item) => (
                    <List.Item
                        actions={[
                            canUpdateModule(MODULE_KEY) && (
                                <Popconfirm
                                    key="edit"
                                    title="Edit this item?"
                                    description="Are you sure you want to edit this item?"
                                    okText="Yes, Edit"
                                    cancelText="Cancel"
                                    onConfirm={() => openEdit(item)}
                                >
                                    <Button type="text" icon={<EditOutlined />} />
                                </Popconfirm>
                            ),

                            canDeleteModule(MODULE_KEY) && (
                                <Popconfirm
                                    key="delete"
                                    title="Delete this item?"
                                    description="Are you sure you want to delete this item?"
                                    okText="Yes, Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDelete(item)}
                                >
                                    <Button type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            ),
                        ].filter(Boolean)}
                    >
                        {item.name}
                    </List.Item>
                )}
            />

            <Modal
                title="Edit Amenity"
                open={isModalOpen}
                onOk={handleEditSave}
                onCancel={() => setIsModalOpen(false)}
                okText="Save"
            >
                <Input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onPressEnter={handleEditSave}
                />
            </Modal>
        </Card>
    )
}

export default ListAllAm
